import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ErrorState, Spinner } from '../components/ui.jsx';
import { LeaseDocument } from '../components/lease.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { saveElementAsPdf } from '../lib/pdf.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/** The agreement on its own, laid out for A4 and the browser's print to PDF. */
const PrintableLease = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { platform } = useMeta();
  const toast = useToast();
  const { data, loading, error, reload } = useFetch(`/tenancies/${id}`, [id]);
  const leaseRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useTitle(data ? `Lease NDK-L${String(data.tenancy.id).padStart(5, '0')}` : 'Lease');

  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  const downloadPDF = async () => {
    // Rendering the sheet to a canvas blocks the page for a moment, so a second
    // click while one run is in flight would only pile up work and look frozen.
    if (!leaseRef.current || !data || saving) return;

    setSaving(true);
    try {
      const leaseNumber = `NDK-L${String(data.tenancy.id).padStart(5, '0')}`;
      await saveElementAsPdf(leaseRef.current, {
        margin: 10,
        filename: `lease-${leaseNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      });
    } catch (cause) {
      console.error('Lease PDF failed:', cause);
      toast.error('Could not build the PDF. Use the Print button and save as PDF instead.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { tenancy } = data;

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          Back
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Print
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={saving}>
          {saving ? 'Preparing PDF...' : '↓ Download PDF'}
        </button>
        {tenancy.status === 'draft' ? (
          <span className="muted">This is still a draft and has not been signed by anybody.</span>
        ) : null}
      </div>

      <div ref={leaseRef} className="lease-sheet">
        <LeaseDocument tenancy={tenancy} platformName={platform?.name} forPrint />
      </div>
    </div>
  );
};

export default PrintableLease;
